import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { salesApi } from '../../services/salesApi.js';
import { todayIso } from '../../utils/formatters.js';
import { CustomerCreateModal } from '../sales/CustomerCreateModal.jsx';
import { EmployeeCreateModal } from '../hr/EmployeeCreateModal.jsx';

export function ProjectCreateModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    customerId: '',
    ownerId: '',
    startDate: todayIso(),
    endDate: '',
    budgetAmount: '',
    hourlyRate: '',
  });

  const customers = useQuery({
    queryKey: ['sales', 'customers', 'for-projects'],
    queryFn: () => salesApi.listCustomers({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'for-projects'],
    queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const customer = (customers.data || []).find((c) => c.id === form.customerId);
    const owner = (employees.data || []).find((emp) => emp.id === form.ownerId);
    onCreate({
      name: form.name,
      customerId: form.customerId || null,
      customerName: customer?.name || null,
      ownerId: form.ownerId || null,
      ownerName: owner ? `${owner.firstName} ${owner.lastName}` : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      budgetAmount: form.budgetAmount || 0,
      hourlyRate: form.hourlyRate || 0,
    });
  };

  return (
    <>
    <Modal open={open} onClose={onClose} title={t('projects.create.title')}>
      <form onSubmit={submit} className="form-stack">
        <div className="field">
          <label className="field-label" htmlFor="p-name">{t('projects.create.name')}</label>
          <input id="p-name" className="input" required value={form.name} onChange={set('name')} />
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="p-customer">{t('projects.create.customer')}</label>
            <div className="inline-create">
              <select id="p-customer" className="select" value={form.customerId} onChange={set('customerId')}>
                <option value="">{t('common.none')}</option>
                {(customers.data || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setCustomerModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="p-owner">{t('projects.create.owner')}</label>
            <div className="inline-create">
              <select id="p-owner" className="select" value={form.ownerId} onChange={set('ownerId')}>
                <option value="">{t('common.none')}</option>
                {(employees.data || []).map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setEmployeeModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="p-start">{t('projects.create.start')}</label>
            <input id="p-start" type="date" className="input" value={form.startDate} onChange={set('startDate')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="p-end">{t('projects.create.end')}</label>
            <input id="p-end" type="date" className="input" value={form.endDate} onChange={set('endDate')} />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="p-budget">{t('projects.create.budget')}</label>
            <input id="p-budget" type="number" min="0" step="0.01" className="input" value={form.budgetAmount} onChange={set('budgetAmount')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="p-rate">{t('projects.billing.rate')}</label>
            <input id="p-rate" type="number" min="0" step="0.01" className="input" value={form.hourlyRate} onChange={set('hourlyRate')} />
          </div>
        </div>

        <div className="page-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn primary">{t('common.create')}</button>
        </div>
      </form>
    </Modal>
    <CustomerCreateModal
      open={customerModalOpen}
      onClose={() => setCustomerModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['sales', 'customers', 'for-projects'] });
        if (created?.id) {
          setForm((f) => ({ ...f, customerId: created.id }));
        }
      }}
    />
    <EmployeeCreateModal
      open={employeeModalOpen}
      onClose={() => setEmployeeModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['hr', 'employees', 'for-projects'] });
        if (created?.id) {
          setForm((f) => ({ ...f, ownerId: created.id }));
        }
      }}
    />
    </>
  );
}
